import { State, process } from '@progress/kendo-data-query';
import { DropDownListChangeEvent } from '@progress/kendo-react-dropdowns';
import { GridDataStateChangeEvent, GridPagerSettings } from '@progress/kendo-react-grid';
import { FC, useCallback, useMemo, useState } from 'react';
import { defaultExcelOption } from 'src/components/ExportExcel';
import GridWrapper, { GridColumn } from 'src/components/GridWrapper';
import DepartmentAssignedMemberCell from 'src/components/custom-cells/DepartmentAssignedMemberCell';
import useViewDepartments from 'src/hooks/useViewDepartments';
import { DepartmentMembers } from 'src/services/useGetDepartmentApi';
import exportToExcel from 'src/utils/exportToExcel';
import { sliceFiveChar } from 'src/utils/strings';
import DepartmentTableModalsToolbar from './DepartmentTableModalsToolbar';

interface Props {
  departmentMembers: DepartmentMembers[];
  selectedDepartmentName: string;
}

const gridPagerSettings: GridPagerSettings = {
  buttonCount: 4,
  pageSizes: [10, 20, 50, 100],
};

const defaultProps = {
  excelFileName: 'Members.xlsx',
  tableTextId: 'Members ',
  DataItemKey: 'id',
  sortable: true,
  resizable: true,
  showColumnMenu: true,
  pageable: gridPagerSettings,
} as const;

const initialMemberDataState: State = {
  skip: 0,
  take: 10,
  sort: [{ field: 'name', dir: 'asc' }],
  filter: {
    logic: 'and',
    filters: [],
  },
};

export const membersColumns: GridColumn<any>[] = [
  {
    field: 'name',
    title: 'Member Name',
    filter: 'text',
    dataType: 'string',
    menu: true,
    sortable: true,
    component: DepartmentAssignedMemberCell,
  },
];

const DepartmentMembersTable: FC<Props> = ({ departmentMembers, selectedDepartmentName }) => {
  const [state, setState] = useState<State>(initialMemberDataState);
  const [excelOption, setExcelOption] = useState(defaultExcelOption);
  const [excelLoading, setExcelLoading] = useState(false);
  const { t, selectedCompany, buId } = useViewDepartments();
  const onDataStateChange = useCallback((e: GridDataStateChangeEvent) => {
    setState(e.dataState);
  }, []);

  const onClearFilter = useCallback(() => {
    setState(initialMemberDataState);
  }, []);

  const { data, total } = useMemo(
    () => process(departmentMembers, state),
    [departmentMembers, state]
  );

  const excelExport = useCallback(
    (data: DepartmentMembers[]) => {
      const columns: string[] = [];
      const columnMapping: Record<string, string> = {};
      columns.push('selectedDepartmentName');
      columnMapping['selectedDepartmentName' as string] = 'Department Name';
      membersColumns.forEach((item) => {
        columns.push(item.field as string);
        Object.assign(columnMapping, { [item.field as string]: item.title });
      });

      const rows = data.map((item) => ({
        selectedDepartmentName: selectedDepartmentName,
        ...item,
      }));
      exportToExcel({
        columns,
        rows,
        fileName: `${buId ? `${buId}_` : ''}${
          sliceFiveChar(selectedCompany?.companyName || '') +
          '_' +
          sliceFiveChar(selectedDepartmentName || '') +
          '_' +
          t('Members') +
          '_'
        } ${new Date().toLocaleString().replace(',', ' ')}`,
        columnMapping,
      });
    },
    [buId, selectedCompany?.companyName, selectedDepartmentName, t]
  );

  const handleExcelChange = useCallback(
    (e: DropDownListChangeEvent) => {
      const selectedOption = e.target.value;
      setExcelOption(selectedOption);
      if (selectedOption.value === 1) {
        setExcelLoading(true);
        excelExport(data);
        setExcelOption(defaultExcelOption);
        setExcelLoading(false);
      } else if (selectedOption.value === 2) {
        const { data } = process(departmentMembers, { filter: state.filter });
        setExcelLoading(true);
        excelExport(data);
        setExcelOption(defaultExcelOption);
        setExcelLoading(false);
      }
    },
    [data, departmentMembers, excelExport, state.filter]
  );

  const gridToolbarChildren = useMemo(
    () => (
      <DepartmentTableModalsToolbar
        onClearFilter={onClearFilter}
        dataState={state}
        excelOption={excelOption}
        handleExcelChange={handleExcelChange}
      />
    ),
    [excelOption, handleExcelChange, onClearFilter, state]
  );

  return (
    <GridWrapper
      id="departmentMemberesTable"
      columns={membersColumns}
      dataSource={data}
      total={total}
      dataState={state}
      onDataStateChange={onDataStateChange}
      style={{ maxHeight: '70vh' }}
      {...defaultProps}
      noRecordText="No Members to display"
      gridToolbarChildren={gridToolbarChildren}
      loading={excelLoading}
    />
  );
};

export default DepartmentMembersTable;
